import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUpNext } from "@/app/modules/ContentDetails/hooks/useUpNext.tsx";
import { Button, ContentThumbnail } from "@/components";
import { cn, getContentThumbnailUrl, getContentTitle } from "@/utils";
import { useNavigateToItem } from "@/hooks";
import { VIDEO_TYPE_AUDIO } from "@/const.ts";
import { Replay } from "@/icons";
import { useTranslate } from "@/providers/context";

interface VideoAutoPlayProps {
  itemId: number;
  startCountdownExternally: boolean;
  onCancel: () => void;
  onReplay: () => void;
  type: string;
}

export const VideoAutoPlay = ({
  itemId,
  type,
  startCountdownExternally,
  onCancel,
  onReplay,
}: VideoAutoPlayProps) => {
  const { translate } = useTranslate();
  const { nextItems, getFullItem, collectionId, courseId} = useUpNext({ itemId });
  const [countdown, setCountdown] = useState(5);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const { navigateToItem } = useNavigateToItem();
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Ref to hold the timer

  const item = nextItems?.length ? nextItems[0] : undefined;
  const nextItem = useMemo(() => {
    if (item) {
      return getFullItem(item);
    }
    return undefined;
  }, [getFullItem, item]);

  const reset = useCallback(() => {
    setIsCountingDown(false);
    setCountdown(5);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, [setCountdown, setIsCountingDown]);

  useEffect(() => {
    return () => {
      reset(); // Clear the timer on unmount
    };
  }, [reset]);

  useEffect(() => {
    if (isCountingDown) {
      // Clear any existing timer before starting a new one
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Delay slightly before starting countdown
      const delayTimer = setTimeout(() => {
        timerRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current!); // Clear the interval when the countdown reaches 0
              return 0;
            }
            return prev - 1;
          });
        }, 1200);
      }, 300);

      return () => {
        clearTimeout(delayTimer); // Clear the initial delay timer
        if (timerRef.current) {
          clearInterval(timerRef.current); // Clear the countdown timer if it's running
        }
      };
    }
  }, [collectionId, isCountingDown, item, navigateToItem]);

  const playNextVideo = useCallback(() => {
    if (item) {
      navigateToItem({
        item,
        parentId: collectionId,
        courseId: courseId,
        autoPlay: true,
      });
    }
  }, [collectionId, item, navigateToItem, courseId]);

  useEffect(() => {
    if (countdown === 0) {
      // Reset the state incase the user browses back
      reset();
      playNextVideo();
    }
  }, [reset, countdown, playNextVideo]);

  useEffect(() => {
    if (startCountdownExternally) {
      setCountdown(5);
      setIsCountingDown(true);
    }
  }, [startCountdownExternally]);

  if (!nextItem) {
    return null;
  }

  const isAudio = type === VIDEO_TYPE_AUDIO;

  return (
    <div
      className={cn(
        `absolute left-0 top-0 flex h-full w-full items-end justify-end bg-black
        pb-20 pr-10 
        transition-opacity duration-[1500ms]
         ${isCountingDown ? "opacity-100" : "opacity-0"} rounded-lg`,
        isAudio && "bg-item-secondary rounded-2xl pb-0 pr-0",
      )}
    >
      {isAudio ? (
        <div className="flex h-full w-full items-center justify-between gap-2">
          <div className="pl-7">
            <Button className="text-grey50" variant="tertiary" onClick={onReplay}>
              <Replay className="mr-1" /> {translate("Replay")}
            </Button>
          </div>
          <div className="flex items-center pr-9">
            <div
              className={cn(
                "mr-5 flex max-w-[188px] flex-col items-start justify-center",
              )}
            >
              <h5 className="caption text-grey50">
                {translate("Up next in")} {countdown}
              </h5>
              <p className="caption-medium text-grey50 mt-1 line-clamp-1">
                {getContentTitle(nextItem)}
              </p>
            </div>
            <Button
              variant="tertiary"
              className="border-border-hover text-grey50 mr-2 border"
              onClick={() => {
                setIsCountingDown(false); // stop the countdown
                onCancel(); // trigger external onCancel function
              }}
            >
              {translate("Cancel")}
            </Button>
            <Button onClick={playNextVideo}>{translate("Play Now")}</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col pb-9">
          <h5 className="h5-bold pb-3 text-white">
            {translate("Up next in")} {countdown}
          </h5>
          <ContentThumbnail
            className="w-[324px] rounded-lg"
            thumbnail={getContentThumbnailUrl(nextItem)}
            contentItem={nextItem}
          />
          <p className="p2-medium mb-6 mt-3 line-clamp-2 text-white">
            {getContentTitle(nextItem)}
          </p>
          <div className="flex w-full gap-2">
            <Button
              variant="tertiary"
              className="border-border-hover flex-grow border text-white"
              onClick={() => {
                setIsCountingDown(false); // stop the countdown
                onCancel(); // trigger external onCancel function
              }}
            >
              {translate("Cancel")}
            </Button>
            <Button className="flex-grow" onClick={playNextVideo}>
              {translate("Play Now")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
